import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { multasAPI } from '../services/api';
import api from '../services/api';
import {
  FileText, ArrowLeft, Calendar, User, Building, Scale,
  TrendingUp, AlertTriangle, CheckCircle, XCircle, Loader2,
  Car, Euro, Clock, MapPin, Info, ExternalLink, Download, FileCheck
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Componente para mostrar el scoring de recurribilidad
function ScoringBadge({ score, recomendacion, loading, error, onAnalizar }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-xl">
        <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
        <span className="text-sm text-slate-500">Analizando...</span>
      </div>
    );
  }

  if (error) {
    return (
      <button
        onClick={onAnalizar}
        className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-xl hover:bg-amber-200 transition-all"
      >
        <AlertTriangle className="w-4 h-4" />
        <span className="text-sm font-medium">Reintentar análisis</span>
      </button>
    );
  }

  if (score === null || score === undefined) {
    return (
      <button
        onClick={onAnalizar}
        className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-all"
      >
        <Scale className="w-4 h-4" />
        <span className="text-sm font-medium">Analizar Recurso</span>
      </button>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 70) return { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle };
    if (score >= 40) return { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertTriangle };
    return { bg: 'bg-rose-100', text: 'text-rose-700', icon: XCircle };
  };

  const { bg, text, icon: Icon } = getScoreColor(score);

  return (
    <div className={`flex items-center gap-2 px-3 py-2 ${bg} rounded-xl`}>
      <Icon className={`w-5 h-5 ${text}`} />
      <div className="text-right">
        <div className={`text-lg font-bold ${text}`}>{score}%</div>
        <div className={`text-xs ${text}`}>
          {recomendacion === 'RECOMENDADO' ? 'Recomendado' :
           recomendacion === 'VALORAR' ? 'Valorar' : 'No recomendado'}
        </div>
      </div>
    </div>
  );
}

function Multas() {
  const [multas, setMultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analisis, setAnalisis] = useState({}); // { multaId: { score, recomendacion, loading } }
  const navigate = useNavigate();
  const { usuario, loading: authLoading } = useAuth();
  const empresa = usuario?.empresa || {};

  useEffect(() => {
    if (authLoading) return;
    if (!empresa.id) {
      navigate('/');
      return;
    }
    cargarMultas();
  }, [authLoading, usuario]);

  const cargarMultas = async () => {
    try {
      const response = await multasAPI.listar({ empresa_id: empresa.id });
      const multasData = response.data.data || [];
      setMultas(multasData);
    } catch (error) {
      console.error('Error cargando multas:', error);
    } finally {
      setLoading(false);
    }
  };

  const analizarMulta = async (multaId) => {
    setAnalisis(prev => ({ ...prev, [multaId]: { loading: true, error: false } }));

    try {
      const response = await api.get(`/recursos/analizar/${multaId}`);

      if (response.data && response.data.success && response.data.analisis) {
        const analisisData = response.data.analisis;

        // Extraer factores de forma segura
        let factoresPos = [];
        let factoresNeg = [];

        if (Array.isArray(analisisData.factores_positivos)) {
          factoresPos = analisisData.factores_positivos.map(f =>
            typeof f === 'string' ? f : (f?.descripcion || f?.texto || f?.factor || JSON.stringify(f))
          ).filter(Boolean);
        }

        if (Array.isArray(analisisData.factores_negativos)) {
          factoresNeg = analisisData.factores_negativos.map(f =>
            typeof f === 'string' ? f : (f?.descripcion || f?.texto || f?.factor || JSON.stringify(f))
          ).filter(Boolean);
        }

        setAnalisis(prev => ({
          ...prev,
          [multaId]: {
            loading: false,
            error: false,
            score: typeof analisisData.probabilidad_exito === 'number' ? analisisData.probabilidad_exito : 0,
            recomendacion: analisisData.recomendacion || 'VALORAR',
            factores_positivos: factoresPos,
            factores_negativos: factoresNeg
          }
        }));
      } else {
        setAnalisis(prev => ({
          ...prev,
          [multaId]: { loading: false, score: 0, error: false, recomendacion: 'VALORAR', factores_positivos: [], factores_negativos: [] }
        }));
      }
    } catch (error) {
      console.error('Error analizando multa:', multaId, error);
      setAnalisis(prev => ({
        ...prev,
        [multaId]: { loading: false, error: true, score: null, factores_positivos: [], factores_negativos: [] }
      }));
    }
  };

  // Estado para descarga de justificantes
  const [descargando, setDescargando] = useState({});

  // Función para descargar justificante
  const descargarJustificante = async (multaId, expediente) => {
    setDescargando(prev => ({ ...prev, [multaId]: true }));
    try {
      const response = await api.get(`/dgt/justificante/${multaId}`);
      if (response.data.success && response.data.download_url) {
        // Abrir URL de descarga en nueva ventana
        window.open(response.data.download_url, '_blank');
      } else {
        alert(response.data.error || 'No se pudo obtener el justificante');
      }
    } catch (error) {
      console.error('Error descargando justificante:', error);
      const mensaje = error.response?.data?.error || 'Error al descargar el justificante';
      alert(mensaje);
    } finally {
      setDescargando(prev => ({ ...prev, [multaId]: false }));
    }
  };

  // Estado y función para descargar PDF de notificación DGT
  const [descargandoNotificacion, setDescargandoNotificacion] = useState({});

  const descargarNotificacionPdf = async (multaId) => {
    setDescargandoNotificacion(prev => ({ ...prev, [multaId]: true }));
    try {
      const response = await api.get(`/dgt/notificacion-pdf/${multaId}`);
      if (response.data.success && response.data.download_url) {
        window.open(response.data.download_url, '_blank');
      } else {
        alert(response.data.error || 'No se pudo obtener el PDF de notificación');
      }
    } catch (error) {
      console.error('Error descargando notificación PDF:', error);
      const mensaje = error.response?.data?.error || 'Error al descargar el PDF de notificación';
      alert(mensaje);
    } finally {
      setDescargandoNotificacion(prev => ({ ...prev, [multaId]: false }));
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0,00€';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white/80 backdrop-blur-lg shadow-soft sticky top-0 z-10 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-slate-100 rounded-xl transition-all duration-200"
            >
              <ArrowLeft className="w-6 h-6 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 font-display">Multas</h1>
              <p className="text-sm text-slate-500">{empresa.nombre}</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/recursos')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all"
          >
            <Scale className="w-5 h-5" />
            <span>Mis Recursos</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Cargando multas...</p>
          </div>
        ) : multas.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl shadow-soft">
            <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FileText className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No hay multas
            </h3>
            <p className="text-slate-500">
              Las multas aparecerán aquí cuando sean notificadas
            </p>
          </div>
        ) : (
          <div className="grid gap-6">
            {multas.map((multa) => {
              const multaAnalisis = analisis[multa.id] || {};

              return (
                <div
                  key={multa.id}
                  className="bg-white rounded-2xl shadow-soft hover:shadow-lg transition-all duration-300 overflow-hidden"
                >
                  {/* Cabecera */}
                  <div className="p-6 border-b border-slate-100">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${
                          multa.estado === 'conductor_identificado' ? 'bg-emerald-100' :
                          multa.estado === 'comunicado_organismo' ? 'bg-blue-100' :
                          'bg-amber-100'
                        }`}>
                          <FileText className={`w-6 h-6 ${
                            multa.estado === 'conductor_identificado' ? 'text-emerald-600' :
                            multa.estado === 'comunicado_organismo' ? 'text-blue-600' :
                            'text-amber-600'
                          }`} />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">
                            {multa.numero_expediente || 'Sin expediente'}
                          </h3>
                          {/* Mostrar número de notificación si es diferente del expediente */}
                          {multa.metadata?.numero_notificacion &&
                           multa.metadata.numero_notificacion !== multa.numero_expediente && (
                            <p className="text-xs text-slate-400 mt-0.5">
                              Notif: {multa.metadata.numero_notificacion}
                            </p>
                          )}
                          <div className="flex items-center gap-3 mt-1">
                            <span className="flex items-center gap-1 text-sm text-slate-500">
                              <Car className="w-4 h-4" />
                              {multa.matricula}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              multa.estado === 'conductor_identificado' ? 'bg-emerald-100 text-emerald-700' :
                              multa.estado === 'comunicado_organismo' ? 'bg-blue-100 text-blue-700' :
                              multa.estado === 'pendiente_identificacion' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {multa.estado === 'conductor_identificado' ? 'Identificada' :
                               multa.estado === 'comunicado_organismo' ? 'Comunicada' :
                               multa.estado === 'pendiente_identificacion' ? 'Pendiente' :
                               multa.estado}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Scoring */}
                      <ScoringBadge
                        score={multaAnalisis.score}
                        recomendacion={multaAnalisis.recomendacion}
                        loading={multaAnalisis.loading}
                        error={multaAnalisis.error}
                        onAnalizar={() => analizarMulta(multa.id)}
                      />
                    </div>
                  </div>

                  {/* Detalles */}
                  <div className="p-6">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {/* Fecha Infracción */}
                      <div>
                        <div className="flex items-center gap-2 text-slate-500 mb-1">
                          <Calendar className="w-4 h-4" />
                          <span className="text-sm font-medium">Fecha Infracción</span>
                        </div>
                        <p className="text-gray-900 font-medium">{formatDate(multa.fecha_infraccion)}</p>
                      </div>

                      {/* Fecha Notificación / Puesta a disposición */}
                      <div>
                        <div className="flex items-center gap-2 text-slate-500 mb-1">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm font-medium">Fecha Notificación</span>
                        </div>
                        <p className="text-gray-900 font-medium">
                          {formatDate(multa.fecha_puesta_disposicion || multa.metadata?.fecha_notificacion)}
                        </p>
                      </div>

                      {/* Importe */}
                      <div>
                        <div className="flex items-center gap-2 text-slate-500 mb-1">
                          <Euro className="w-4 h-4" />
                          <span className="text-sm font-medium">Importe</span>
                        </div>
                        <p className="text-xl font-bold text-gray-900">{formatCurrency(multa.importe_multa)}</p>
                        {multa.puntos > 0 && (
                          <p className="text-sm text-rose-600 font-medium">-{multa.puntos} puntos</p>
                        )}
                      </div>

                      {/* Conductor */}
                      <div>
                        <div className="flex items-center gap-2 text-slate-500 mb-1">
                          <User className="w-4 h-4" />
                          <span className="text-sm font-medium">Conductor</span>
                        </div>
                        {multa.tipo_identificacion === 'empresa_cesionaria' ? (
                          <div>
                            <p className="text-gray-900 font-medium">{multa.empresa_cesionaria_razon_social}</p>
                            <p className="text-sm text-slate-500">{multa.empresa_cesionaria_cif}</p>
                            <span className="text-xs text-emerald-600">Empresa cesionaria</span>
                          </div>
                        ) : multa.conductor_nombre ? (
                          <div>
                            <p className="text-gray-900 font-medium">{multa.conductor_nombre}</p>
                            <p className="text-sm text-slate-500">{multa.conductor_dni}</p>
                          </div>
                        ) : (
                          <p className="text-amber-600 font-medium">Pendiente identificar</p>
                        )}
                      </div>
                    </div>

                    {/* Fila adicional con más detalles */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-4 pt-4 border-t border-slate-100">
                      {/* Organismo */}
                      <div>
                        <div className="flex items-center gap-2 text-slate-500 mb-1">
                          <Building className="w-4 h-4" />
                          <span className="text-sm font-medium">Organismo</span>
                        </div>
                        <p className="text-gray-900 text-sm">{multa.organismo_emisor || 'N/A'}</p>
                      </div>

                      {/* Ubicación/Lugar (si existe) */}
                      {(multa.lugar || multa.metadata?.lugar) && (
                        <div>
                          <div className="flex items-center gap-2 text-slate-500 mb-1">
                            <MapPin className="w-4 h-4" />
                            <span className="text-sm font-medium">Lugar</span>
                          </div>
                          <p className="text-gray-900 text-sm">{multa.lugar || multa.metadata?.lugar}</p>
                        </div>
                      )}

                      {/* Artículo infringido */}
                      {multa.articulo && (
                        <div>
                          <div className="flex items-center gap-2 text-slate-500 mb-1">
                            <FileText className="w-4 h-4" />
                            <span className="text-sm font-medium">Artículo</span>
                          </div>
                          <p className="text-gray-900 text-sm font-mono">{multa.articulo}</p>
                        </div>
                      )}

                      {/* Código organismo */}
                      {multa.codigo_organismo_emisor && (
                        <div>
                          <div className="flex items-center gap-2 text-slate-500 mb-1">
                            <Info className="w-4 h-4" />
                            <span className="text-sm font-medium">Código Org.</span>
                          </div>
                          <p className="text-gray-900 text-sm font-mono">{multa.codigo_organismo_emisor}</p>
                        </div>
                      )}
                    </div>

                    {/* Descripción de la infracción (concepto) */}
                    {(multa.concepto || multa.articulo || multa.puntos) && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                        <div className="flex items-center gap-2 text-blue-700 mb-2">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-sm font-medium">Hecho Denunciado (Infracción)</span>
                        </div>
                        {multa.concepto ? (
                          <p className="text-gray-800 font-medium">{multa.concepto}</p>
                        ) : (
                          <p className="text-amber-600 italic">Pendiente extracción del PDF</p>
                        )}
                        {(multa.articulo || multa.puntos) && (
                          <div className="flex items-center gap-4 mt-2 text-sm text-blue-600">
                            {multa.articulo && <span>📜 {multa.articulo}</span>}
                            {multa.puntos && <span>⚠️ {multa.puntos} puntos</span>}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Observaciones adicionales (si son diferentes del concepto) */}
                    {multa.observaciones && multa.observaciones !== multa.concepto && (
                      <div className="mt-4 p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2 text-slate-500 mb-2">
                          <Info className="w-4 h-4" />
                          <span className="text-sm font-medium">Observaciones</span>
                        </div>
                        <p className="text-gray-700 text-sm">{multa.observaciones}</p>
                      </div>
                    )}

                    {/* Factores del análisis */}
                    {multaAnalisis.score !== undefined && multaAnalisis.score !== null && !multaAnalisis.loading && !multaAnalisis.error && (
                      <div className="mt-4 grid md:grid-cols-2 gap-4">
                        {/* Factores Positivos */}
                        {Array.isArray(multaAnalisis.factores_positivos) && multaAnalisis.factores_positivos.length > 0 && (
                          <div className="p-4 bg-emerald-50 rounded-xl">
                            <h4 className="flex items-center gap-2 text-emerald-700 font-medium mb-2">
                              <CheckCircle className="w-4 h-4" />
                              A favor del recurso
                            </h4>
                            <ul className="space-y-1">
                              {multaAnalisis.factores_positivos.slice(0, 3).map((factor, i) => (
                                <li key={i} className="text-sm text-emerald-700">• {String(factor)}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Factores Negativos */}
                        {Array.isArray(multaAnalisis.factores_negativos) && multaAnalisis.factores_negativos.length > 0 && (
                          <div className="p-4 bg-rose-50 rounded-xl">
                            <h4 className="flex items-center gap-2 text-rose-700 font-medium mb-2">
                              <XCircle className="w-4 h-4" />
                              En contra del recurso
                            </h4>
                            <ul className="space-y-1">
                              {multaAnalisis.factores_negativos.slice(0, 3).map((factor, i) => (
                                <li key={i} className="text-sm text-rose-700">• {String(factor)}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Acciones */}
                  <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-500">
                        <Clock className="w-4 h-4 inline mr-1" />
                        Registrada: {formatDate(multa.created_at)}
                        {/* Mostrar número de registro DGT si existe */}
                        {multa.numero_registro_dgt && (
                          <span className="ml-3 text-emerald-600">
                            <FileCheck className="w-4 h-4 inline mr-1" />
                            Reg. DGT: {multa.numero_registro_dgt}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-3">
                        {/* Botón de descarga de PDF de notificación DGT */}
                        {multa.notificacion_pdf_url && (
                          <button
                            onClick={() => descargarNotificacionPdf(multa.id)}
                            disabled={descargandoNotificacion[multa.id]}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-xl hover:bg-slate-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Descargar PDF original de notificación DGT"
                          >
                            {descargandoNotificacion[multa.id] ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Descargando...
                              </>
                            ) : (
                              <>
                                <FileText className="w-4 h-4" />
                                Notificación DGT
                              </>
                            )}
                          </button>
                        )}
                        {/* Botón de descarga de justificante para multas comunicadas */}
                        {multa.estado === 'comunicado_organismo' && (
                          <button
                            onClick={() => descargarJustificante(multa.id, multa.numero_expediente)}
                            disabled={descargando[multa.id]}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {descargando[multa.id] ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Descargando...
                              </>
                            ) : (
                              <>
                                <Download className="w-4 h-4" />
                                Justificante
                              </>
                            )}
                          </button>
                        )}
                        {multaAnalisis.score !== undefined && multaAnalisis.score >= 20 && (
                          <button
                            onClick={() => navigate('/recursos')}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-medium"
                          >
                            <Scale className="w-4 h-4" />
                            Solicitar Recurso
                          </button>
                        )}
                        {multaAnalisis.score !== undefined && multaAnalisis.score < 20 && (
                          <span className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-600 rounded-xl">
                            <XCircle className="w-4 h-4" />
                            No recomendado recurrir
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

export default Multas;
